import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import qs from 'query-string'

import type { components, operations } from './generated/schema'

// Type aliases for convenience
// Note: Extending Agent type with 'active' field from backend (not yet in generated schema)
export type Agent = components['schemas']['Agent'] & {
  /** Whether the agent is active (sync triggers enabled) */
  active?: boolean
  /** Free-text note for support staff */
  note?: string | null
  /** Whether OAuth has completed. Only set for partners whose tokens live in the agent config */
  oauthAuthorized?: boolean
}
export type CreateAgentRequest = components['schemas']['CreateAgentRequest'] & {
  /** Whether the agent should be created in active state (defaults to false/inactive if omitted) */
  active?: boolean
  /** Free-text note for support staff */
  note?: string | null
}
export type UpdateAgentRequest = components['schemas']['UpdateAgentRequest'] & {
  /** Free-text note for support staff */
  note?: string | null
}
export type AgentConfig = components['schemas']['AgentConfig']
export type PartnerEnum = components['schemas']['PartnerEnum']
// `detail` is the DRF-style key the mango-api proxy returns on 4xx errors; the
// generated schema only models the native `message`/`code` shape.
export type ApiError = components['schemas']['Error'] & {
  detail?: string
}
export type Pagination = components['schemas']['Pagination']

// Query parameters for listAgents
export type ListAgentsParams = operations['listAgents']['parameters']['query']

// Response types
export type ListAgentsResponse = {
  agents?: Agent[]
  pagination?: Pagination
}

export type CheckAgentDuplicatesRequest = {
  company_id: string
  partner: string
  config?: Record<string, unknown>
}

export type CheckAgentDuplicatesResponse = {
  exists: boolean
  matchingCount: number
  agents: Agent[]
}

export type SyncJobResponse = {
  status: string
  jobId: string
  message: string
}

export type PauseResumeResponse = {
  message: string
}

// Callback types for authentication
export type GetAuthDataCallback = () => {
  accessToken: string | null
}

// Custom API Response types
export interface ApiOkResponse<T> {
  ok: true
  status: number
  data: T
}

export interface ApiErrorResponse<E> {
  ok: false
  status?: number
  data?: E
  originalError?: Error
}

export type ApiResponse<T, E = T> = ApiOkResponse<T> | ApiErrorResponse<E>

const create = (baseURL: string) => {
  const axiosInstance: AxiosInstance = axios.create({
    baseURL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    timeout: 10 * 1000, // 10 sec.
  })

  let getAuthData: GetAuthDataCallback | undefined

  const setCallbacks = (getAuthDataCallback: GetAuthDataCallback) => {
    getAuthData = getAuthDataCallback
  }

  const getAuthHeaders = (): Record<string, string> => {
    const { accessToken: jwt } = getAuthData?.() ?? {}
    return jwt ? { Authorization: `Bearer ${jwt}` } : {}
  }

  /**
   * Helper to make authenticated requests and format responses
   */
  const makeRequest = async <T, E = ApiError>(
    config: AxiosRequestConfig,
  ): Promise<ApiResponse<T, E>> => {
    try {
      const response = await axiosInstance.request<T>({
        ...config,
        headers: {
          ...config.headers,
          ...getAuthHeaders(),
        },
      })

      return {
        ok: true,
        status: response.status,
        data: response.data,
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<E>
        return {
          ok: false,
          status: axiosError.response?.status,
          data: axiosError.response?.data,
          originalError: axiosError,
        }
      }

      return {
        ok: false,
        originalError: error as Error,
      }
    }
  }

  /**
   * List all agents
   * Currently proxied through mango-api at /internal/v1/integration-agents/
   * Will migrate to direct Integrations API call once VPC Lattice is configured
   * @param params - Optional query parameters for filtering and pagination
   * @returns Promise with agents list and pagination metadata
   */
  const listAgents = async (
    params?: ListAgentsParams,
  ): Promise<ApiResponse<ListAgentsResponse, ApiError>> => {
    const queryString = params ? qs.stringify(params) : ''
    // Note: Django REST Framework routers require trailing slash before query params
    // e.g., /integration-agents/?partner=dentally (not /integration-agents?partner=dentally)
    const url = queryString
      ? `/internal/v1/integration-agents/?${queryString}`
      : '/internal/v1/integration-agents/'

    return makeRequest<ListAgentsResponse, ApiError>({
      method: 'GET',
      url,
    })
  }

  /**
   * Get a single agent by ID
   * Currently proxied through mango-api at /internal/v1/integration-agents/{agentId}/
   * Will migrate to direct Integrations API call once VPC Lattice is configured
   * @param agentId - The unique agent identifier (UUID)
   * @returns Promise with agent details
   */
  const getAgent = async (
    agentId: string,
  ): Promise<ApiResponse<Agent, ApiError>> => {
    return makeRequest<Agent, ApiError>({
      method: 'GET',
      url: `/internal/v1/integration-agents/${encodeURIComponent(agentId)}/`,
    })
  }

  /**
   * Create a new agent configuration
   * Currently proxied through mango-api at /internal/v1/integration-agents/
   * Will migrate to direct Integrations API call once VPC Lattice is configured
   *
   * Note: This method automatically generates a UUID for agentId if not provided.
   * Per agreement with Grove team, client generates the agent identifier to minimize
   * Lambda logic - they just validate and store the request.
   *
   * @param request - Agent configuration data (agentId optional, will be generated if omitted)
   * @returns Promise with created agent
   */
  const createAgent = async (
    request: CreateAgentRequest,
  ): Promise<ApiResponse<Agent, ApiError>> => {
    // Generate UUID for agentId if not provided (client responsibility)
    const agentId = request.agentId || crypto.randomUUID()
    const requestWithId = { ...request, agentId }

    return makeRequest<Agent, ApiError>({
      method: 'POST',
      url: '/internal/v1/integration-agents/',
      data: requestWithId,
    })
  }

  /**
   * Check for duplicate integration agents before creation
   * @param request - company_id, partner, and optional config to narrow matching
   * @returns Promise with exists flag, matchingCount, and matching agents
   */
  const checkAgentDuplicates = async (
    request: CheckAgentDuplicatesRequest,
  ): Promise<ApiResponse<CheckAgentDuplicatesResponse, ApiError>> => {
    return makeRequest<CheckAgentDuplicatesResponse, ApiError>({
      method: 'POST',
      url: '/internal/v1/integration-agents/check/',
      data: request,
    })
  }

  /**
   * Trigger sync workflow for an integration agent
   * Currently proxied through mango-api at /internal/v1/integration-agents/{agentId}/sync/
   * Will migrate to direct Integrations API call once VPC Lattice is configured
   * @param agentId - The unique agent identifier (UUID)
   * @returns Promise with sync job response
   */
  const createSyncJob = async (
    agentId: string,
  ): Promise<ApiResponse<SyncJobResponse, ApiError>> => {
    return makeRequest<SyncJobResponse, ApiError>({
      method: 'POST',
      url: `/internal/v1/integration-agents/${agentId}/sync/`,
    })
  }

  /**
   * Update an existing integration agent
   * Currently proxied through mango-api at /internal/v1/integration-agents/{agentId}/
   * Will migrate to direct Integrations API call once VPC Lattice is configured
   * @param agentId - The unique agent identifier (UUID)
   * @param request - Partial agent update data (companyId and/or config)
   * @returns Promise with updated agent
   */
  const updateAgent = async (
    agentId: string,
    request: UpdateAgentRequest,
  ): Promise<ApiResponse<Agent, ApiError>> => {
    return makeRequest<Agent, ApiError>({
      method: 'PATCH',
      url: `/internal/v1/integration-agents/${agentId}/`,
      data: request,
    })
  }

  /**
   * Pause an integration agent (stop sync triggers)
   * Currently proxied through mango-api at /internal/v1/integration-agents/{agentId}/pause/
   * Will migrate to direct Integrations API call once VPC Lattice is configured
   * @param agentId - The unique agent identifier (UUID)
   * @returns Promise with pause response
   */
  const pauseAgent = async (
    agentId: string,
  ): Promise<ApiResponse<PauseResumeResponse, ApiError>> => {
    return makeRequest<PauseResumeResponse, ApiError>({
      method: 'POST',
      url: `/internal/v1/integration-agents/${agentId}/pause/`,
    })
  }

  /**
   * Resume an integration agent (restart sync triggers)
   * Currently proxied through mango-api at /internal/v1/integration-agents/{agentId}/resume/
   * Will migrate to direct Integrations API call once VPC Lattice is configured
   * @param agentId - The unique agent identifier (UUID)
   * @returns Promise with resume response
   */
  const resumeAgent = async (
    agentId: string,
  ): Promise<ApiResponse<PauseResumeResponse, ApiError>> => {
    return makeRequest<PauseResumeResponse, ApiError>({
      method: 'POST',
      url: `/internal/v1/integration-agents/${agentId}/resume/`,
    })
  }

  return {
    setCallbacks,
    listAgents,
    getAgent,
    createAgent,
    checkAgentDuplicates,
    createSyncJob,
    updateAgent,
    pauseAgent,
    resumeAgent,
  }
}

export default {
  create,
}
